import { useAuth, useClerk } from "@clerk/tanstack-react-start";
import { useQuery } from "@tanstack/react-query";
import {
  Outlet,
  createFileRoute,
  isRedirect,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import React, { useEffect, useState } from "react";
import { z } from "zod";

import { AuthModal } from "~/components/auth-modal";
import { CoachCard, type CoachCardMinimalData } from "~/components/coach-card";
import { CoachCardSkeleton } from "~/components/coach-card-skeleton";
import { CoachLayout } from "~/components/layouts/coach-layout";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Modal } from "~/components/ui/modal/modal";
import { ProfileSkeleton } from "~/components/ui/profile-skeleton";
import { SimpleCarousel } from "~/components/ui/simple-carousel";
import { useNavigationLoading } from "~/hooks/use-navigation-loading";
import { coachQueryOptions } from "~/lib/query-options";
import { CoachDiscoverySchema } from "~/lib/schemas/coach-discovery";
import { isCoachIndexable } from "~/lib/shared/coach-indexability";
import { createCoachMeta, createPersonStructuredData } from "~/lib/shared/meta";

const DISCOVERY_MATCH_LIMIT = 12;

/**
 * Matches for the visitor's saved discovery answers. `getDiscoveryPreferences`
 * decides whose answers those are. The loader catches a failure here and
 * renders without preloaded matches.
 */
export const getSavedPreferenceMatches = createServerFn({
  method: "GET",
}).handler(async (): Promise<CoachCardMinimalData[]> => {
  const [
    { getDiscoveryPreferences },
    { findCoachMatches, toCoachCardData },
    { transformUserPreferencesToCoachMatchForm },
  ] = await Promise.all([
    import("~/lib/server/discovery-visitor"),
    import("~/lib/server/coach-matching"),
    import("~/lib/transforms/user-preferences"),
  ]);

  const preferences = await getDiscoveryPreferences();
  if (!preferences) {
    return [];
  }

  const matches = await findCoachMatches({
    preferences: transformUserPreferencesToCoachMatchForm(preferences),
    limit: DISCOVERY_MATCH_LIMIT,
  });
  return matches.map(toCoachCardData);
});

const localPreferenceMatchesInput = z.object({
  preferences: CoachDiscoverySchema,
});

/**
 * Matches for discovery answers kept in this browser. The schema has no
 * embedding field, so these rank without vector similarity.
 */
export const getLocalPreferenceMatches = createServerFn({ method: "POST" })
  .validator((data: unknown) => localPreferenceMatchesInput.parse(data))
  .handler(async ({ data }): Promise<CoachCardMinimalData[]> => {
    const { findCoachMatches, toCoachCardData } =
      await import("~/lib/server/coach-matching");
    const matches = await findCoachMatches({
      preferences: data.preferences,
      limit: DISCOVERY_MATCH_LIMIT,
    });
    return matches.map(toCoachCardData);
  });

export const Route = createFileRoute("/_public/discovery/$username")({
  loader: async ({ context, params }) => {
    try {
      const coach = await context.queryClient.ensureQueryData(
        coachQueryOptions.byUsername(params.username),
      );

      if (!coach) {
        throw redirect({ to: "/discovery" });
      }

      // Preload matches when this visitor has saved discovery answers
      let preloadedMatches: CoachCardMinimalData[] = [];
      try {
        preloadedMatches = await getSavedPreferenceMatches();
      } catch {
        // Failed to preload matches, continue without them
      }

      return {
        coach,
        preloadedMatches,
      };
    } catch (error) {
      // Redirects must reach the router untouched — most importantly the
      // Clerk handshake rethrown by the coach-visibility gate for a stale
      // owner session (and the missing-coach redirect above). Anything else
      // (incl. the gate's notFound) falls back to the discovery index.
      if (isRedirect(error)) throw error;
      throw redirect({ to: "/discovery" });
    }
  },
  head: ({ loaderData, params }) => {
    const coach = loaderData?.coach;
    if (!coach) {
      return {
        title: "Coach Discovery | Dream Coach Match",
        meta: [
          {
            name: "description",
            content:
              "Discover your ideal coach through our personalized matching system",
          },
        ],
      };
    }

    const description = `Explore ${coach.fullName}'s coaching profile through our discovery system. ${coach.profile?.description ? coach.profile.description.slice(0, 160) + "..." : "Discover how this coach can help you achieve your goals."}`;
    const imageUrl = coach.profile?.mainImage || coach.profilePictureUrl;
    const currentUrl = `https://dreamcoachmatch.com/discovery/${params.username}`;

    const metaTags = createCoachMeta(coach.fullName, {
      description,
      ogDescription: description,
      ogImage: imageUrl || undefined,
      ogUrl: currentUrl,
      ogType: "profile",
      twitterCard: imageUrl ? "summary_large_image" : "summary",
      twitterImage: imageUrl || undefined,
      // This page is a discovery-flow presentation of the main profile —
      // canonicalize to the profile so the two don't compete in search.
      canonical: `https://dreamcoachmatch.com/${coach.username}`,
      author: coach.fullName,
      keywords: [
        "coach discovery",
        "coaching",
        "personalized match",
        coach.profile?.coachingTitle || "life coach",
        "coaching consultation",
      ],
    });

    // Add structured data for the person/coach
    const structuredData = createPersonStructuredData({
      name: coach.fullName,
      jobTitle: coach.profile?.coachingTitle || "Life Coach",
      image: imageUrl || undefined,
      url: currentUrl,
      description: coach.profile?.description || description,
    });

    const shouldIndex = isCoachIndexable(coach);

    return {
      ...metaTags,
      title: `Discover ${coach.fullName} - Coach Discovery | Dream Coach Match`,
      meta: [
        ...(metaTags.meta ?? []),
        ...(shouldIndex
          ? []
          : [{ name: "robots", content: "noindex, nofollow" }]),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(structuredData),
        },
      ],
    };
  },
  component: DiscoveryLayout,
});

function DiscoveryLayout() {
  const { username } = Route.useParams();
  const { coach, preloadedMatches } = Route.useLoaderData();
  const navigate = useNavigate();
  const clerk = useClerk();
  const auth = useAuth();
  const isLoading = useNavigationLoading();
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [matches, setMatches] = useState<CoachCardMinimalData[]>(
    preloadedMatches || [],
  );
  const [isLoadingMatches, setIsLoadingMatches] = useState(
    !preloadedMatches || preloadedMatches.length === 0,
  );

  const { data: queryCoach } = useQuery({
    ...coachQueryOptions.byUsername(username),
    initialData: coach,
  });

  // Load matches client-side from localStorage only if we don't have preloaded matches
  useEffect(() => {
    // If we already have preloaded matches, skip localStorage loading
    if (preloadedMatches && preloadedMatches.length > 0) {
      setIsLoadingMatches(false);
      return;
    }

    const loadMatches = async () => {
      try {
        const storedPreferences = localStorage.getItem("userPreferences");

        if (!storedPreferences) {
          // No preferences found - redirect to discovery flow
          navigate({ to: "/discovery/steps", replace: true });
          return;
        }

        const preferences: unknown = JSON.parse(storedPreferences);
        setMatches(await getLocalPreferenceMatches({ data: { preferences } }));
      } catch {
        // Failed to load matches, loading state will be reset in finally
      } finally {
        setIsLoadingMatches(false);
      }
    };

    loadMatches();
  }, [username, navigate, preloadedMatches]);

  const renderItem = (
    matchData: CoachCardMinimalData,
    index: number,
    isActive: boolean,
  ) => {
    const isAuthenticated = Boolean(auth.isSignedIn || clerk?.isSignedIn);
    const isEnabled = isAuthenticated || [2, 3].includes(index);

    return (
      <CoachCard
        coachData={{
          coach: matchData.coach,
          profile: matchData.profile || null,
          similarity: matchData.similarity,
          expertise: matchData.expertise,
        }}
        isActive={isActive}
        isEnabled={isEnabled}
        href={`/discovery/${matchData.coach.username}`}
        source="discovery_matches"
        onLockClick={() => setShowSignupModal(true)}
        className="overflow-hidden"
      />
    );
  };

  const renderSkeleton = (_: unknown, i: number) => (
    <CoachCardSkeleton className="animate-in fade-in" seed={i} />
  );

  if (!queryCoach) {
    return <div>No coach data found</div>;
  }

  return (
    <CoachLayout username={username} coach={queryCoach}>
      <div className="mx-auto my-4 flex w-full max-w-screen-xl flex-col overflow-visible px-4 md:mt-8">
        <Breadcrumb
          className="mb-6"
          items={[
            { label: "Home", href: "/" },
            { label: "Coach Discovery", href: "/discovery" },
          ]}
        />
        <h2 className="mb-6 font-heading text-3xl font-medium tracking-tight">
          Your Recommended Coaches
        </h2>
        {!isLoadingMatches && matches.length > 0 ? (
          <SimpleCarousel
            items={matches}
            itemWidth={380}
            selectedIndex={(() => {
              const currentCoachIndex = matches.findIndex(
                (match) => match.coach.username === queryCoach?.username,
              );
              const isAuthenticated = auth.isSignedIn || clerk?.isSignedIn;

              // If coach not found in results, use defaults
              if (currentCoachIndex === -1) {
                return isAuthenticated ? 0 : 2;
              }

              // For authenticated users, show the coach from URL
              if (isAuthenticated) {
                return currentCoachIndex;
              }

              // For anonymous users, only allow indices 2 or 3
              if (currentCoachIndex === 2 || currentCoachIndex === 3) {
                return currentCoachIndex;
              }

              // Anonymous user with URL pointing to restricted coach - show index 2
              return 2;
            })()}
            renderItem={renderItem}
          />
        ) : (
          <SimpleCarousel
            items={Array.from({ length: 10 }).fill(null)}
            itemWidth={380}
            selectedIndex={2}
            renderItem={renderSkeleton}
          />
        )}
      </div>

      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        {isLoading ? <ProfileSkeleton /> : <Outlet />}
      </div>

      <Modal.Root open={showSignupModal} onOpenChange={setShowSignupModal}>
        <Modal.Panel size="2xl" className="max-h-[90vh] overflow-y-auto">
          <AuthModal
            isModalView={true}
            onSuccess={() => {
              setShowSignupModal(false);
              navigate({ to: "/coach-finder" });
            }}
          />
        </Modal.Panel>
      </Modal.Root>
    </CoachLayout>
  );
}
